import Compressor from 'compressorjs';

export interface ImageCompressionOptions {
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  mimeType?: string;
  convertTypes?: string[];
  convertSize?: number;
  retainExif?: boolean;
  checkOrientation?: boolean;
  outputFormat?: 'jpeg' | 'webp' | 'png';
}

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

/**
 * Сжимает изображение с помощью compressorjs
 * @param file - Файл изображения для сжатия
 * @param options - Опции сжатия
 * @returns Promise с результатом сжатия
 */
export function compressImage(
  file: File,
  options: ImageCompressionOptions = {}
): Promise<CompressionResult> {
  const {
    quality = 0.8,
    maxWidth = 1920,
    maxHeight = 1080,
    mimeType = 'auto',
    convertTypes = ['image/png'],
    convertSize = 5000000, // 5MB
    retainExif = false,
    checkOrientation = true,
    outputFormat = 'jpeg',
  } = options;

  return new Promise((resolve, reject) => {
    const originalSize = file.size;
    
            console.log('Starting image compression:', {
          originalSize,
          fileName: file.name,
          fileType: file.type,
          options: { quality, maxWidth, maxHeight, mimeType, outputFormat }
        });

        // Проверяем, что входной файл валиден
        if (!file || file.size === 0) {
          console.error('Input file is invalid:', file);
          reject(new Error('Invalid input file'));
          return;
        }

    new Compressor(file, {
      quality,
      maxWidth,
      maxHeight,
      mimeType,
      convertTypes,
      convertSize,
      retainExif,
      checkOrientation,
      success: (result) => {
        const compressedFile = result as File;
        const compressedSize = compressedFile.size;
        const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;

        console.log('Compression successful:', {
          originalSize,
          compressedSize,
          compressionRatio: compressionRatio.toFixed(2) + '%',
          fileName: compressedFile.name,
          fileType: compressedFile.type
        });

        // Определяем MIME тип на основе outputFormat
        let finalMimeType = 'image/jpeg';
        if (outputFormat === 'webp') {
          finalMimeType = 'image/webp';
        } else if (outputFormat === 'png') {
          finalMimeType = 'image/png';
        }

        // Убеждаемся, что файл имеет правильный MIME тип
        const finalFile = new File([compressedFile], compressedFile.name, {
          type: finalMimeType
        });

        console.log('Final file after compression:', {
          fileName: finalFile.name,
          fileSize: finalFile.size,
          fileType: finalFile.type
        });

        // Проверяем, что файл не пустой
        if (finalFile.size === 0) {
          console.error('Compressed file is empty, using original file');
          resolve({
            file: file,
            originalSize,
            compressedSize: originalSize,
            compressionRatio: 0,
          });
          return;
        }

        // Проверяем, что файл не слишком большой
        if (finalFile.size > 10 * 1024 * 1024) { // 10MB
          console.error('Compressed file is too large, using original file');
          resolve({
            file: file,
            originalSize,
            compressedSize: originalSize,
            compressionRatio: 0,
          });
          return;
        }

        // Проверяем, что файл имеет правильный MIME тип
        if (finalFile.type !== finalMimeType) {
          console.error('Compressed file has wrong MIME type:', finalFile.type);
          resolve({
            file: file,
            originalSize,
            compressedSize: originalSize,
            compressionRatio: 0,
          });
          return;
        }

        resolve({
          file: finalFile,
          originalSize,
          compressedSize,
          compressionRatio,
        });
      },
      error: (err) => {
        console.error('Compression failed:', err);
        reject(err);
      },
    });
  });
}

/**
 * Сжимает изображение с настройками по умолчанию для веб-приложений
 * @param file - Файл изображения для сжатия
 * @returns Promise с результатом сжатия
 */
export function compressImageForWeb(file: File): Promise<CompressionResult> {
  return compressImage(file, {
    quality: 0.4,
    maxWidth: 1920,
    maxHeight: 1080,
    mimeType: 'image/jpeg',
    convertTypes: ['image/png', 'image/webp'],
    convertSize: 100000, // 2MB
    retainExif: false,
    checkOrientation: true,
  });
}

/**
 * Сжимает изображение с высоким качеством для превью
 * @param file - Файл изображения для сжатия
 * @returns Promise с результатом сжатия
 */
export function compressImageForPreview(file: File): Promise<CompressionResult> {
  return compressImage(file, {
    quality: 0.9,
    maxWidth: 800,
    maxHeight: 600,
    mimeType: 'image/jpeg',
    convertTypes: ['image/png', 'image/webp'],
    convertSize: 1000000, // 1MB
    retainExif: false,
    checkOrientation: true,
  });
}

/**
 * Сжимает изображение с агрессивными настройками для экономии места
 * @param file - Файл изображения для сжатия
 * @returns Promise с результатом сжатия
 */
export function compressImageAggressive(file: File): Promise<CompressionResult> {
  return compressImage(file, {
    quality: 0.6,
    maxWidth: 1200,
    maxHeight: 800,
    mimeType: 'image/jpeg',
    convertTypes: ['image/png', 'image/webp'],
    convertSize: 1000000, // 1MB
    retainExif: false,
    checkOrientation: true,
  });
}

/**
 * Сжимает изображение в формат WebP
 * @param file - Файл изображения для сжатия
 * @returns Promise с результатом сжатия
 */
export function compressImageToWebP(file: File): Promise<CompressionResult> {
  console.log('compressImageToWebP called with file:', {
    name: file.name,
    type: file.type,
    size: file.size
  });
  return compressImage(file, {
    quality: 0.8,
    maxWidth: 1920,
    maxHeight: 1080,
    mimeType: 'image/webp',
    convertTypes: ['image/png', 'image/jpeg'],
    convertSize: 100000, // 100KB
    retainExif: false,
    checkOrientation: true,
    outputFormat: 'webp',
  });
} 